<template>
  <!-- Bounding box type: SVG overlay with cutouts -->
  <svg
    v-if="isVisible && isBoundingBox && transformedBoxes.length > 0"
    class="pointer-events-none absolute inset-0 h-full w-full"
    data-testid="areas-of-interest-overlay"
    preserveAspectRatio="none"
  >
    <defs>
      <mask :id="svgMaskId">
        <!-- White = show overlay everywhere -->
        <rect width="100%" height="100%" fill="white" />
        <!-- Black rects = punch transparent cutouts for each bounding box -->
        <rect
          v-for="(box, idx) in transformedBoxes"
          :key="idx"
          :x="`${box.x}%`"
          :y="`${box.y}%`"
          :width="`${box.width}%`"
          :height="`${box.height}%`"
          fill="black"
        />
      </mask>
    </defs>
    <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.7)" :mask="`url(#${svgMaskId})`" />
  </svg>

  <!-- Mask type: Canvas overlay with pixel transparency filter -->
  <canvas
    v-if="isVisible && isMask && effectiveMaskUrl"
    ref="canvasRef"
    class="pointer-events-none absolute inset-0 h-full w-full"
    data-testid="areas-of-interest-mask-overlay"
  />
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";

defineOptions({ name: "AreasOfInterestOverlay" });

const WHITE_THRESHOLD = 200;

const props = defineProps({
  areasOfInterest: { type: Object, required: true },
  naturalWidth: { type: Number, required: true },
  naturalHeight: { type: Number, required: true },
  isVisible: { type: Boolean, default: true },
  pinId: { type: String, required: true },
  segmentationMaskUrl: { type: String, default: undefined }
});

// --- Type guards (matching React isBoundingBox2D / isMaskObject) ---

function isBoundingBox2D(obj) {
  return obj != null && "bbox2d" in obj && Array.isArray(obj.bbox2d);
}

function isMaskObject(obj) {
  return obj != null && "maskUrl" in obj;
}

// --- Shared ---

const isBoundingBox = computed(() => props.areasOfInterest?.type === "bounding_box");
const isMask = computed(() => props.areasOfInterest?.type === "mask");

// --- Bounding box overlay ---

const svgMaskId = computed(() => `aoi-mask-${props.pinId}`);

const transformedBoxes = computed(() => {
  if (!isBoundingBox.value) return [];
  if (!props.naturalWidth || !props.naturalHeight) return [];

  return props.areasOfInterest.objects.filter(isBoundingBox2D).map(obj => {
    const [x1, y1, x2, y2] = obj.bbox2d;
    return {
      x: (x1 / props.naturalWidth) * 100,
      y: (y1 / props.naturalHeight) * 100,
      width: ((x2 - x1) / props.naturalWidth) * 100,
      height: ((y2 - y1) / props.naturalHeight) * 100
    };
  });
});

// --- Mask overlay ---

const canvasRef = ref(null);
const maskImageLoaded = ref(null);
let maskResizeObserver = null;

// Resolve effective mask URL: segmentationMaskUrl overrides mask object's maskUrl
const effectiveMaskUrl = computed(() => {
  if (props.segmentationMaskUrl) return props.segmentationMaskUrl;
  const obj = props.areasOfInterest?.objects?.find(isMaskObject);
  return obj?.maskUrl ?? null;
});

/**
 * Pixel filter that makes white pixels transparent and sets non-white to 70% opacity.
 * Matches the Konva maskTransparencyFilter from the Design Lab version.
 */
function applyMaskTransparencyFilter(imageData) {
  const { data } = imageData;
  const len = data.length;

  for (let i = 0; i < len; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    if (r >= WHITE_THRESHOLD && g >= WHITE_THRESHOLD && b >= WHITE_THRESHOLD) {
      // White pixels (area of interest) -> transparent
      data[i + 3] = 0;
    } else {
      // Non-white pixels -> 70% opacity overlay
      data[i + 3] = 178;
    }
  }
}

function drawMask() {
  const canvas = canvasRef.value;
  const img = maskImageLoaded.value;
  if (!canvas || !img) return;

  const displayWidth = Math.round(canvas.clientWidth);
  const displayHeight = Math.round(canvas.clientHeight);
  if (!displayWidth || !displayHeight) return;

  canvas.width = displayWidth;
  canvas.height = displayHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.drawImage(img, 0, 0, displayWidth, displayHeight);

  try {
    const imageData = ctx.getImageData(0, 0, displayWidth, displayHeight);
    applyMaskTransparencyFilter(imageData);
    ctx.putImageData(imageData, 0, 0);
  } catch {
    // CORS / SecurityError — canvas tainted, pixel filter cannot be applied
  }
}

function loadMaskImage(url) {
  if (!url) {
    maskImageLoaded.value = null;
    return;
  }

  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    maskImageLoaded.value = img;
  };
  img.onerror = () => {
    maskImageLoaded.value = null;
  };
  img.src = url;
}

// Load mask image when URL changes
watch(effectiveMaskUrl, url => loadMaskImage(url), { immediate: true });

// Draw when canvas mounts or image loads; observe canvas for resize redraws
watch(canvasRef, el => {
  if (maskResizeObserver) {
    maskResizeObserver.disconnect();
    maskResizeObserver = null;
  }

  if (el) {
    maskResizeObserver = new ResizeObserver(() => {
      if (maskImageLoaded.value) drawMask();
    });
    maskResizeObserver.observe(el);

    // Initial draw if image is already loaded
    if (maskImageLoaded.value) drawMask();
  }
});

// Redraw when image finishes loading (canvas may already be mounted)
watch(maskImageLoaded, img => {
  if (img && canvasRef.value) drawMask();
});

onBeforeUnmount(() => {
  if (maskResizeObserver) {
    maskResizeObserver.disconnect();
    maskResizeObserver = null;
  }
  maskImageLoaded.value = null;
});
</script>
